import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

import { getTimeZoneOptions, getGuessedTimezoneFull } from 'utils/time'
import { capitalizeFirstLetter } from 'utils/strings'

import homePageTypes from 'constants/homePageTypes'
import { AVAILABLE_LOCALES } from 'constants/constants'

import IconFlag from 'components/Shared/IconFlag/Index'
import PageForm from 'components/Shared/PageForm/Index'


const ProfileForm = props => {
  const { onSubmit } = props
  const isAnyAdmin = useSelector(sessionUserSelector.isAnyAdmin)

  const initialValues = {
    'firstName': useSelector(sessionUserSelector.firstName),
    'lastName': useSelector(sessionUserSelector.lastName),
    'email': useSelector(sessionUserSelector.email),
    'autosubscriptionEnabled': useSelector(sessionUserSelector.autosubscriptionEnabled),
    'timezone': useSelector(sessionUserSelector.timezone),
    'homePage': useSelector(sessionUserSelector.homePage),
    'locale': useSelector(sessionUserSelector.locale),
    'timeFormat24': useSelector(sessionUserSelector.timeFormat24),
    'timezoneAutodetect': useSelector(sessionUserSelector.timezoneAutodetect),
    'newReleasesSubscriptionEnabled': useSelector(sessionUserSelector.newReleasesSubscriptionEnabled),
  }

  const userRole = useSelector(sessionUserSelector.role)
  const capability = useSelector(sessionUserSelector.capability)
  const userPartnerName = useSelector(sessionUserSelector.partnerName)

  const noTicketingSystemsWithApiAccess = useSelector(ticketingSystemsSelector.noTicketingSystemsWithApiAccess)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const onCancel = (mutators) => {
    navigate(-1)
  }

  const iconComponentsMap = {}
  AVAILABLE_LOCALES.forEach(locale => {
    iconComponentsMap[locale.value] = <IconFlag flagName={locale.value} />
  })

  const guessedTimezone = getGuessedTimezoneFull()
  const myAccountFormFieldParams = [
    {
      name: 'firstName',
      label: t('profilePage.firstName'),
      type: 'text',
    },
    {
      name: 'lastName',
      label: t('profilePage.lastName'),
      type: 'text',
    },
    {
      name: 'email',
      label: t('profilePage.email'),
      type: 'text',
      required: true,
    },
    {
      name: 'role',
      label: t('profilePage.role'),
      type: 'readOnlyField',
      readOnlyFieldValue: capitalizeFirstLetter(userRole),
    },
    {
      name: 'capability',
      label: t('profilePage.capability'),
      type: 'readOnlyField',
      readOnlyFieldValue: capitalizeFirstLetter(capability),
    },
    {
      name: 'partner',
      label: t('profilePage.partner'),
      type: 'readOnlyField',
      readOnlyFieldValue: capitalizeFirstLetter(userPartnerName),
    },
  ]

  const myPreferencesFormFieldParams = [
    {
      name: 'timeFormat24',
      label: t('profilePage.useTimeFormat24'),
      type: 'switch',
      checkboxLabel: t('profilePage.time24Hour'),
    },
    {
      name: 'timezoneAutodetect',
      label: t('profilePage.timezoneAutodetect'),
      type: 'switch',
      checkboxLabel: guessedTimezone,
      hideLabelWhenFalse: true,
    },
    {
      name: 'timezone',
      label: t('profilePage.timeZone'),
      type: 'select',
      options: getTimeZoneOptions(),
      displayCondition: {
        fieldName: 'timezoneAutodetect',
        fieldValue: false,
      },
    },
  ]

  const mySubscriptionsFormFieldParams = [
    {
      name: 'autosubscriptionEnabled',
      label: t('profilePage.autoSubscribe'),
      type: 'switch',
    },
  ]

  const newReleaseSubscriptionField = {
    name: 'newReleasesSubscriptionEnabled',
    label: t('profilePage.subscribeToNewReleases'),
    type: 'switch',
  }
  mySubscriptionsFormFieldParams.push(newReleaseSubscriptionField)

  const localeField =
    {
      name: 'locale',
      label: t('profilePage.locale'),
      type: 'select',
      options: AVAILABLE_LOCALES,
      iconComponentsMap: iconComponentsMap,
    }

  const homePageField =
    {
      name: 'homePage',
      label: t('profilePage.dashboard'),
      type: 'toggleButtonGroup',
      buttons: [
        { label: t('profilePage.switcherText1'), value: homePageTypes.TICKETS },
        { label: t('profilePage.switcherText2'), value: homePageTypes.STATS },
      ],
    }

  if (initialValues.firstName === 'de') {
    myPreferencesFormFieldParams.push(localeField)
  }

  if (initialValues.homePage && !noTicketingSystemsWithApiAccess && !isAnyAdmin) {
    myPreferencesFormFieldParams.push(homePageField)
  }

  const fieldParams = [
    {
      group: t('profilePage.myAccount'),
      fieldParams: myAccountFormFieldParams,
    },
    {
      group: t('profilePage.myPreferences'),
      fieldParams: myPreferencesFormFieldParams,
    },
    {
      group: t('profilePage.mySubscriptions'),
      fieldParams: mySubscriptionsFormFieldParams,
    },
  ]

  return (
    <PageForm
      flatFieldNames
      onSubmit={onSubmit}
      onCancel={onCancel}
      fieldParams={fieldParams}
      initialValues={initialValues}
      actionButtonTitle={t('common.update')}
    />
  )
}

ProfileForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
}

export default ProfileForm
